/**
 * Smooth the players and the ball between the server's ~10 Hz frames (ADR-0044). Each frame carries the game's own
 * clock (`t`, ms since kickoff), so frames are placed on the server's timeline and drawn a little in the past
 * (`delayMs`), blending between the two frames around that moment: late or bunched packets do not make players
 * stutter. A player who jumped (a reset between plays) is not dragged across the field: frames more than JUMP_METRES
 * apart snap. Before kickoff (t = 0) the line-up is shown as it is.
 */
export interface ArenaFrame {
  t: number;
  /** x, z (metres), facing (radians), animation per player. */
  pos: Float32Array;
  ball: [number, number, number] | null;
}

export interface ArenaSample {
  /** x, z, facing, animation per player, and the speed (m/s) for the run cycle. */
  pos: Float32Array;
  speed: Float32Array;
  ball: [number, number, number] | null;
}

const KEEP_MS = 3000;
const MAX_EXTRAPOLATE_MS = 200;
/** A player moving more than this between two frames was placed, not run: no blending. */
export const JUMP_METRES = 6;

function lerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

/** Decode the state's centimetre / centiradian numbers. */
export function decodePositions(pos: readonly number[]): Float32Array {
  const out = new Float32Array(pos.length);
  for (let i = 0; i < pos.length; i += 4) {
    out[i] = pos[i]! / 100;
    out[i + 1] = pos[i + 1]! / 100;
    out[i + 2] = pos[i + 2]! / 100;
    out[i + 3] = pos[i + 3]!;
  }
  return out;
}

export class ArenaTimeline {
  private frames: ArenaFrame[] = [];
  private key = "";
  private offset: number | null = null;

  constructor(readonly delayMs = 180) {}

  /** A shared state arrived at local time `localNow`. */
  push(state: { gameNo: number; phase: string; t: number; pos: readonly number[]; ball: [number, number, number] | null }, localNow: number): void {
    const key = `${state.gameNo}:${state.phase === "pregame" ? "pre" : "game"}`;
    if (key !== this.key) {
      this.frames = [];
      this.offset = null;
      this.key = key;
    }
    const frame: ArenaFrame = { t: state.t, pos: decodePositions(state.pos), ball: state.ball ? [state.ball[0] / 100, state.ball[1] / 100, state.ball[2] / 100] : null };
    const last = this.frames[this.frames.length - 1];
    if (last && state.t < last.t) return;
    if (last && state.t === last.t) {
      this.frames[this.frames.length - 1] = frame;
    } else this.frames.push(frame);
    const o = localNow - state.t;
    this.offset = this.offset === null || o < this.offset ? o : this.offset + (o - this.offset) * 0.05;
    const newest = this.frames[this.frames.length - 1]!.t;
    while (this.frames.length > 2 && this.frames[0]!.t < newest - KEEP_MS) this.frames.shift();
  }

  get size(): number {
    return this.frames.length;
  }

  sample(localNow: number): ArenaSample | null {
    const f = this.frames;
    if (f.length === 0 || this.offset === null) return null;
    const clock = localNow - this.offset - this.delayMs;
    const first = f[0]!;
    const last = f[f.length - 1]!;
    if (f.length === 1 || clock <= first.t) return { pos: first.pos.slice(), speed: new Float32Array(first.pos.length / 4), ball: first.ball };
    if (clock >= last.t) {
      const prev = f[f.length - 2]!;
      const ahead = Math.min(clock - last.t, MAX_EXTRAPOLATE_MS) / Math.max(1, last.t - prev.t);
      return blend(prev, last, 1 + ahead, true);
    }
    let i = 0;
    while (i + 2 < f.length && f[i + 1]!.t <= clock) i++;
    const a = f[i]!;
    const b = f[i + 1]!;
    return blend(a, b, (clock - a.t) / Math.max(1, b.t - a.t), false);
  }
}

function blend(a: ArenaFrame, b: ArenaFrame, t: number, extrapolating: boolean): ArenaSample {
  const n = Math.min(a.pos.length, b.pos.length);
  const pos = new Float32Array(n);
  const speed = new Float32Array(n / 4);
  const dt = Math.max(1, b.t - a.t) / 1000;
  for (let i = 0; i < n; i += 4) {
    const dx = b.pos[i]! - a.pos[i]!;
    const dz = b.pos[i + 1]! - a.pos[i + 1]!;
    const d = Math.hypot(dx, dz);
    const jump = d > JUMP_METRES;
    const k = jump ? 1 : extrapolating ? Math.min(t, 1.15) : t;
    pos[i] = a.pos[i]! + dx * k;
    pos[i + 1] = a.pos[i + 1]! + dz * k;
    pos[i + 2] = jump ? b.pos[i + 2]! : lerpAngle(a.pos[i + 2]!, b.pos[i + 2]!, Math.min(1, t));
    pos[i + 3] = t < 0.5 ? a.pos[i + 3]! : b.pos[i + 3]!;
    speed[i / 4] = jump ? 0 : d / dt;
  }
  let ball: [number, number, number] | null = b.ball;
  if (a.ball && b.ball) {
    const d = Math.hypot(b.ball[0] - a.ball[0], b.ball[2] - a.ball[2]);
    const k = d > 30 ? 1 : extrapolating ? Math.min(t, 1.15) : t;
    ball = [a.ball[0] + (b.ball[0] - a.ball[0]) * k, a.ball[1] + (b.ball[1] - a.ball[1]) * k, a.ball[2] + (b.ball[2] - a.ball[2]) * k];
  }
  return { pos, speed, ball };
}
